/**
 * Setup: what a stack has switched on and connected.
 *
 * Everything on this tab is as it stands at the last nightly update, so no
 * number here follows the time window. The first section says so once and no
 * figure repeats it.
 */
import { useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import type { ClientSnapshot } from '@/types/fleetAnalytics';
import {
  isReady,
  kpiProps,
  needsOf,
  selectCounts,
  selectInventory,
  selectProvisionedUsers,
  selectSetup,
} from '../selectors';
import type { Figure, Inventory, Setup as SetupAnswer, SetupRow, Switches } from '../selectors';
import { fmtN } from '../shared';
import { Empty, Fact, FactGroup, Facts, Kpi, KpiGrid, NeedsTier, Panel, Section } from '../ui/kit';
import { Bar, LinkButton, plural, Quiet, SearchBox, sortRows, SortTh, useSort } from './common';
import type { SortValue } from './common';
import type { DashboardTabProps } from './registry';
import './Setup.css';

// ─── words ───────────────────────────────────────────────────────────────

const FILES_SEARCH: Record<NonNullable<Switches['filesSearch']>, string> = {
  bedrock: 'Bedrock',
  q: 'Q Business',
  off: 'Off',
};

/** An integration's stored name as its product name: `google_calendar` reads "Google Calendar". */
const integrationName = (app: string) => app.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** A figure's props with no period tag: the section says once that everything here is as of now. */
function tile<T>(figure: Figure<T>, format: (value: NonNullable<T>) => string) {
  const { value, approx, note } = kpiProps(figure, format);
  return { value, approx, note };
}

// ─── the tab ─────────────────────────────────────────────────────────────

export function Setup({ data, fleet, selection, onSelectClient }: DashboardTabProps) {
  const counts = useMemo(() => selectCounts(data), [data]);
  const provisioned = useMemo(() => selectProvisionedUsers(data), [data]);
  const setup = useMemo(() => selectSetup(selection.stacks), [selection.stacks]);
  const rows = setup.rows;
  const isView = selection.kind === 'view';
  const withIntegration = counts.peopleWithIntegrations.value;

  return (
    <>
      <Section
        title="Connected"
        note="Everything on this tab is as it stands at the last nightly update. The time window does not apply."
      >
        <KpiGrid cols={withIntegration === null ? 4 : 5}>
          <Kpi label="Provisioned people" {...tile(provisioned, fmtN)} sub="People with a login" />
          <Kpi label="Knowledge bases" {...tile(counts.knowledgeBases, fmtN)} />
          <Kpi
            label="Data connectors"
            {...tile(counts.dataConnectors, fmtN)}
            sub="Outside file stores people connected"
          />
          <Kpi
            label="Integrations in use"
            {...tile(counts.distinctIntegrations, fmtN)}
            sub={plural(counts.integrationConnections.value, 'connection')}
          />
          {/* Older nightly records do not hold this count. The figure shows once they do. */}
          {withIntegration !== null && (
            <Kpi
              label="People with an integration"
              {...tile(counts.peopleWithIntegrations, fmtN)}
              sub={`of ${fmtN(provisioned.value)} provisioned`}
            />
          )}
        </KpiGrid>
      </Section>

      {isView ? (
        <>
          <div className="dash-cols">
            <SwitchCounts setup={setup} />
            <IntegrationsPanel fleet={fleet} stacks={selection.stacks} isView />
          </div>
          <StacksPanel rows={rows} onSelectClient={onSelectClient} />
        </>
      ) : (
        <div className="dash-cols">
          {rows[0] && <SwitchList switches={rows[0].switches} />}
          <IntegrationsPanel fleet={fleet} stacks={selection.stacks} isView={false} />
          <KnowledgeBasesPanel fleet={fleet} stacks={selection.stacks} />
        </div>
      )}
    </>
  );
}

// ─── switches ────────────────────────────────────────────────────────────

/** One stack's switches, a row each. */
function SwitchList({ switches }: { switches: Switches }) {
  const { appBuilder, integrations, creditsShown, filesSearch, lendsQuota, usesQuotaOf } = switches;
  return (
    <Panel title="Switched on">
      <Facts cols={1}>
        <FactGroup>
          {/* Older nightly records do not say. The row shows once they do. */}
          {appBuilder !== null && (
            <Fact
              label="App Builder"
              value={appBuilder ? 'On' : 'Off'}
              hint={appBuilder ? 'People can build apps.' : 'People can publish dashboards only.'}
            />
          )}
          <Fact
            label="Integrations"
            value={integrations ? 'On' : 'Off'}
            hint={integrations ? 'People can connect outside tools.' : 'This stack has no integrations service.'}
          />
          <Fact
            label="Credits page"
            value={creditsShown ? 'Shown' : 'Hidden'}
            hint={
              creditsShown
                ? 'People see their credits and the count in chat.'
                : 'People do not see credits. Use is still metered.'
            }
          />
          <Fact
            label="Files search"
            value={filesSearch ? FILES_SEARCH[filesSearch] : 'Not set'}
            hint={
              filesSearch === 'off' ? 'Search in Numa Files is switched off.' : 'What search in Numa Files runs on.'
            }
          />
          <Fact
            label="Model quota"
            value={usesQuotaOf ? `Borrowed from ${usesQuotaOf}` : lendsQuota ? 'Lent' : 'Its own'}
            hint={
              usesQuotaOf
                ? 'Model use runs on the quota of another AWS account.'
                : lendsQuota
                  ? 'Other stacks can run their model use on this account.'
                  : 'Model use runs on this account, for this stack only.'
            }
          />
        </FactGroup>
      </Facts>
    </Panel>
  );
}

/** The same switches across a view: how many stacks have each one on. */
function SwitchCounts({ setup }: { setup: SetupAnswer }) {
  const { on } = setup;
  const all = setup.rows.length;
  const builderKnown = all - on.appBuilderUnknown;
  const of = (n: number) => `${fmtN(n)} of ${fmtN(all)}`;

  return (
    <Panel title="Switched on" subtitle={`Across ${plural(all, 'stack')}.`}>
      <Facts cols={1}>
        <FactGroup>
          {/* Older nightly records do not say. The row shows once one stack or more does. */}
          {builderKnown > 0 && (
            <Fact
              label="App Builder on"
              value={`${fmtN(on.appBuilder)} of ${fmtN(builderKnown)}`}
              hint={`Stacks where people can build apps. Without it they can publish dashboards only.${on.appBuilderUnknown ? ` ${plural(on.appBuilderUnknown, 'stack does', 'stacks do')} not record it yet.` : ''}`}
            />
          )}
          <Fact
            label="Integrations on"
            value={of(on.integrations)}
            hint="Stacks with the integrations service, so people can connect outside tools."
          />
          <Fact
            label="Credits page shown"
            value={of(on.creditsShown)}
            hint="Stacks where people see their credits. Use is metered either way."
          />
          <Fact label="Files search on Bedrock" value={of(on.filesSearch.bedrock)} />
          <Fact label="Files search on Q Business" value={of(on.filesSearch.q)} />
          <Fact label="Files search off" value={of(on.filesSearch.off)} />
          {on.filesSearch.unknown > 0 && <Fact label="Files search not set" value={of(on.filesSearch.unknown)} />}
          <Fact
            label="Lending model quota"
            value={of(on.lendsQuota)}
            hint="Stacks whose AWS account other stacks can run their model use on."
          />
          <Fact
            label="Using another account's model quota"
            value={of(on.usesAnothersQuota)}
            hint="Stacks whose model use runs on another AWS account's quota."
          />
        </FactGroup>
      </Facts>
    </Panel>
  );
}

// ─── integrations in use ─────────────────────────────────────────────────

interface InventoryPanelProps {
  fleet: DashboardTabProps['fleet'];
  stacks: ClientSnapshot[];
}

function IntegrationsPanel({ fleet, stacks, isView }: InventoryPanelProps & { isView: boolean }) {
  const answer = useMemo(() => selectInventory(stacks), [stacks]);
  const rows: Inventory['integrations'] | null = isReady(answer) ? answer.integrations : null;
  // The longest bar is the tool with the most people. Every other bar is drawn against it.
  const most = rows ? Math.max(0, ...rows.map((r) => r.people)) : 0;

  return (
    <Panel
      title="Integrations in use"
      subtitle={rows ? `${plural(rows.length, 'outside tool')} with one person or more connected.` : undefined}
      flush
    >
      <NeedsTier fleet={fleet} tiers={rows ? ['inventory'] : needsOf(answer)} what="The list of integrations">
        {rows && rows.length === 0 ? (
          <Empty>Nobody has connected an outside tool yet.</Empty>
        ) : (
          <div className="dash-scroll dash-scroll--short">
            <Table className="dash-table">
              <thead>
                <tr>
                  <th>Integration</th>
                  <th aria-hidden="true" />
                  <th className="text-end">People connected</th>
                  {isView && <th className="text-end">Stacks</th>}
                </tr>
              </thead>
              <tbody>
                {(rows || []).map((r) => (
                  <tr key={r.app}>
                    <td className="dash-name">{integrationName(r.app)}</td>
                    <td className="setup-bar">
                      <Bar fraction={most > 0 ? r.people / most : 0} className="setup-bar__bar" />
                    </td>
                    <td className="text-end numeric">{fmtN(r.people)}</td>
                    {isView && <td className="text-end numeric">{fmtN(r.stacks)}</td>}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </NeedsTier>
    </Panel>
  );
}

// ─── knowledge bases (one stack) ─────────────────────────────────────────

function KnowledgeBasesPanel({ fleet, stacks }: InventoryPanelProps) {
  const answer = useMemo(() => selectInventory(stacks), [stacks]);
  const [query, setQuery] = useState('');
  const all: Inventory['knowledgeBases'] | null = isReady(answer) ? answer.knowledgeBases : null;
  // The kind is drawn only when the record knows it for one knowledge base or more.
  const showKind = Boolean(all?.some((k) => k.kind && k.kind !== 'unknown'));

  const visible = useMemo(() => {
    if (!all) return [];
    const q = query.trim().toLowerCase();
    const rows = q ? all.filter((k) => (k.name || '').toLowerCase().includes(q)) : all;
    return sortRows(rows, (k) => k.name, 'asc');
  }, [all, query]);

  return (
    <Panel
      title="Knowledge bases"
      subtitle={all ? `${fmtN(visible.length)} of ${plural(all.length, 'knowledge base')}.` : undefined}
      flush
      actions={
        all && all.length > 30 ? (
          <SearchBox value={query} onChange={setQuery} placeholder="Search knowledge bases" />
        ) : undefined
      }
    >
      <NeedsTier fleet={fleet} tiers={all ? ['inventory'] : needsOf(answer)} what="The list of knowledge bases">
        {visible.length === 0 ? (
          <Empty>{all && all.length > 0 ? 'No knowledge base matches.' : 'No knowledge bases yet.'}</Empty>
        ) : (
          <div className="dash-scroll dash-scroll--short">
            <Table className="dash-table">
              <thead>
                <tr>
                  <th>Name</th>
                  {showKind && <th>Kind</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((k, i) => (
                  // The nightly record can list one knowledge base twice, so its id alone is not a key.
                  <tr key={`${k.kb_id || k.name}:${i}`}>
                    <td className="dash-name dash-wrap">{k.name || <Quiet>No name</Quiet>}</td>
                    {showKind && <td>{k.kind && k.kind !== 'unknown' ? k.kind : <Quiet />}</td>}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </NeedsTier>
    </Panel>
  );
}

// ─── stacks by what they have on (a view only) ───────────────────────────

type StackColumn =
  | 'label'
  | 'people'
  | 'knowledgeBases'
  | 'connectors'
  | 'integrations'
  | 'withIntegration'
  | 'appBuilder'
  | 'credits'
  | 'files'
  | 'quota';
const STACK_WORD_COLUMNS: ReadonlySet<StackColumn> = new Set<StackColumn>(['label', 'files']);

const onOff = (on: boolean | null): number | null => (on === null ? null : on ? 1 : 0);

const STACK_VALUE: Record<StackColumn, (r: SetupRow) => SortValue> = {
  label: (r) => r.label,
  people: (r) => r.provisioned.value,
  knowledgeBases: (r) => r.counts.knowledgeBases.value,
  connectors: (r) => r.counts.dataConnectors.value,
  integrations: (r) => (r.switches.integrations ? r.counts.distinctIntegrations.value : null),
  withIntegration: (r) => r.counts.peopleWithIntegrations.value,
  appBuilder: (r) => onOff(r.switches.appBuilder),
  credits: (r) => onOff(r.switches.creditsShown),
  files: (r) => (r.switches.filesSearch ? FILES_SEARCH[r.switches.filesSearch] : null),
  quota: (r) => (r.switches.usesQuotaOf ? 1 : r.switches.lendsQuota ? 2 : 0),
};

function StacksPanel({
  rows,
  onSelectClient,
}: {
  rows: SetupRow[];
  onSelectClient: DashboardTabProps['onSelectClient'];
}) {
  const [query, setQuery] = useState('');
  const { sort, onSort } = useSort<StackColumn>({ key: 'people', dir: 'desc' }, STACK_WORD_COLUMNS);
  // A column whose every row would read "not recorded" is left out.
  const showBuilder = rows.some((r) => r.switches.appBuilder !== null);
  const showWithIntegration = rows.some((r) => r.counts.peopleWithIntegrations.value !== null);
  // The same for model quota when every stack runs on its own: the list above says none lends.
  const showQuota = rows.some((r) => r.switches.lendsQuota || r.switches.usesQuotaOf !== null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = q ? rows.filter((r) => r.label.toLowerCase().includes(q)) : rows;
    return sortRows(matching, STACK_VALUE[sort.key], sort.dir);
  }, [rows, query, sort]);

  const columns = 7 + (showBuilder ? 1 : 0) + (showWithIntegration ? 1 : 0) + (showQuota ? 1 : 0);

  return (
    <Panel
      title="Stacks by what they have on"
      subtitle={`${fmtN(visible.length)} of ${plural(rows.length, 'stack')}. Select a row to open the stack.`}
      flush
      actions={
        rows.length > 30 ? <SearchBox value={query} onChange={setQuery} placeholder="Search stacks" /> : undefined
      }
    >
      <div className="dash-scroll">
        <Table className="dash-table">
          <thead>
            <tr>
              <SortTh column="label" sort={sort} onSort={onSort}>
                Stack
              </SortTh>
              <SortTh column="people" hint="People with a login." numeric sort={sort} onSort={onSort}>
                People
              </SortTh>
              <SortTh column="knowledgeBases" numeric sort={sort} onSort={onSort}>
                Knowledge bases
              </SortTh>
              <SortTh column="connectors" numeric sort={sort} onSort={onSort}>
                Data connectors
              </SortTh>
              <SortTh
                column="integrations"
                hint="Outside tools with one person or more connected. Off: the stack has no integrations service."
                numeric
                sort={sort}
                onSort={onSort}
              >
                Integrations
              </SortTh>
              {showWithIntegration && (
                <SortTh
                  column="withIntegration"
                  hint="People with one connected integration or more."
                  numeric
                  sort={sort}
                  onSort={onSort}
                >
                  People connected
                </SortTh>
              )}
              {showBuilder && (
                <SortTh column="appBuilder" sort={sort} onSort={onSort}>
                  App Builder
                </SortTh>
              )}
              <SortTh column="credits" sort={sort} onSort={onSort}>
                Credits page
              </SortTh>
              <SortTh column="files" sort={sort} onSort={onSort}>
                Files search
              </SortTh>
              {showQuota && (
                <SortTh
                  column="quota"
                  hint="Lent: other stacks can run their model use on this account. Borrowed: this stack runs on another account's quota."
                  sort={sort}
                  onSort={onSort}
                >
                  Model quota
                </SortTh>
              )}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={columns}>
                  <Empty>No stack matches.</Empty>
                </td>
              </tr>
            )}
            {visible.map((r) => {
              const s = r.switches;
              const withIntegration = r.counts.peopleWithIntegrations.value;
              return (
                <tr key={r.client} className="dash-row" onClick={() => onSelectClient(r.client)}>
                  <td>
                    {/* The row is the click target. The button gives it to the keyboard. */}
                    <LinkButton name title={`Open ${r.label}`}>
                      {r.label}
                    </LinkButton>
                  </td>
                  <td className="text-end numeric">{fmtN(r.provisioned.value)}</td>
                  <td className="text-end numeric">{fmtN(r.counts.knowledgeBases.value)}</td>
                  <td className="text-end numeric">{fmtN(r.counts.dataConnectors.value)}</td>
                  <td className="text-end numeric">
                    {s.integrations ? fmtN(r.counts.distinctIntegrations.value) : <Quiet>Off</Quiet>}
                  </td>
                  {showWithIntegration && (
                    <td className="text-end numeric">{withIntegration === null ? <Quiet /> : fmtN(withIntegration)}</td>
                  )}
                  {showBuilder && (
                    <td>
                      {s.appBuilder === null ? <Quiet>Not recorded</Quiet> : s.appBuilder ? 'On' : <Quiet>Off</Quiet>}
                    </td>
                  )}
                  <td>{s.creditsShown ? 'Shown' : <Quiet>Hidden</Quiet>}</td>
                  <td>
                    {s.filesSearch === null ? (
                      <Quiet>Not set</Quiet>
                    ) : s.filesSearch === 'off' ? (
                      <Quiet>Off</Quiet>
                    ) : (
                      FILES_SEARCH[s.filesSearch]
                    )}
                  </td>
                  {showQuota && <td>{s.usesQuotaOf ? 'Borrowed' : s.lendsQuota ? 'Lent' : <Quiet>Its own</Quiet>}</td>}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Panel>
  );
}
